import { FunctionComponent } from 'react'
import { useIsMobile } from 'src/hooks/useIsMobile'
import './CoreUxLoadingSkeleton.scss'

export enum SkeletonType {
  HERO = 'HERO',
  QUICK_FILTERS = 'QUICK_FILTERS',
  CATEGORY_FILTERS = 'CATEGORY_FILTERS',
  SWIMLANE_SINGLE = 'SWIMLANE_SINGLE',
  SWIMLANE_DOUBLE = 'SWIMLANE_DOUBLE',
  BANNER = 'BANNER',
  CARD = 'CARD'
}

interface CoreUxLoadingSkeletonProps {
  type?: SkeletonType
}

export const FallbackSkeleton: FunctionComponent = () => {
  return (
    <div className="core-ux-skeleton" data-testid="fallback-skeleton">
      <div className="core-ux-fallback-skeleton">
        <div className="core-ux-skeleton__shape core-ux-fallback-skeleton__rectangle" />
        <div className="core-ux-skeleton__shape core-ux-fallback-skeleton__rectangle" />
        <div className="core-ux-skeleton__shape core-ux-fallback-skeleton__rectangle" />
        <div className="core-ux-skeleton__shape core-ux-fallback-skeleton__rectangle" />
      </div>
    </div>
  )
}

export const SectionTitleSkeleton: FunctionComponent = () => {
  return (
    <div className="core-ux-skeleton--section-title">
      <div className="core-ux-skeleton__shape core-ux-skeleton--section-title__first-rectangle" />
      <div className="core-ux-skeleton__shape core-ux-skeleton--section-title__second-rectangle" />
    </div>
  )
}

export const SwimlaneSingleItem = () => {
  return (
    <div className="core-ux-skeleton--swimlane-single-item">
      <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__big-square" />
      <div className="core-ux-skeleton--swimlane-single-item__rectangles-section">
        <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__small-rectangle" />
        <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__large-rectangle" />
      </div>
      <div className="core-ux-skeleton--swimlane-single-item__rectangle-and-cirles-section">
        <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__medium-rectangle" />
        <div className="core-ux-skeleton--swimlane-single-item__circles-container">
          <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__small-circle" />
          <div className="core-ux-skeleton__shape core-ux-skeleton--swimlane-single-item__small-circle" />
        </div>
      </div>
    </div>
  )
}

export const SwimlaneSingleSkeleton: FunctionComponent = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div
        className="core-ux-skeleton core-ux-skeleton--swimlane-single"
        data-testid="swimlane-single-skeleton">
        <SectionTitleSkeleton />
        <div className="core-ux-skeleton--swimlane-single__items-container">
          <SwimlaneSingleItem />
          <SwimlaneSingleItem />
          <SwimlaneSingleItem />
        </div>
      </div>
    )
  }

  return <FallbackSkeleton />
}

export const CategoryFiltersSkeleton: FunctionComponent = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div
        className="core-ux-skeleton"
        data-testid="category-filters-skeleton-mobile">
        <div className="core-ux-skeleton--category-filters">
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
          <div className="core-ux-skeleton core-ux-skeleton__shape" />
        </div>
      </div>
    )
  }

  return <FallbackSkeleton />
}

export const QuickFilterItem = () => {
  return (
    <div className="core-ux-skeleton--quick-filter">
      <div className="core-ux-skeleton__shape core-ux-skeleton--quick-filter__circle" />
      <div className="core-ux-skeleton__shape core-ux-skeleton--quick-filter__rectangle" />
    </div>
  )
}

export const QuickFiltersSkeleton: FunctionComponent = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div
        className="core-ux-skeleton core-ux-skeleton--quick-filters"
        data-testid="quick-filters-skeleton">
        <QuickFilterItem />
        <QuickFilterItem />
        <QuickFilterItem />
        <QuickFilterItem />
        <QuickFilterItem />
        <QuickFilterItem />
      </div>
    )
  }

  return <FallbackSkeleton />
}

export const HeroSkeleton = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div
        className="core-ux-skeleton core-ux-skeleton--hero"
        data-testid="hero-skeleton-mobile">
        <div className="core-ux-skeleton--hero__left-column">
          <div className="core-ux-skeleton--hero__top-section">
            <div className="core-ux-skeleton__shape core-ux-skeleton__shape--dark core-ux-skeleton--hero__large-rectangle" />
            <div className="core-ux-skeleton__shape core-ux-skeleton__shape--dark core-ux-skeleton--hero__medium-rectangle" />
          </div>
          <div className="core-ux-skeleton__shape core-ux-skeleton__shape--dark core-ux-skeleton--hero__small-rectangle" />
        </div>
        <div className="core-ux-skeleton--hero__square" />
      </div>
    )
  }

  return (
    <div className="core-ux-skeleton" data-testid="hero-skeleton-desktop">
      <div className="core-ux-skeleton__shape core-ux-skeleton--hero" />
    </div>
  )
}

export const CoreUxLoadingSkeleton: FunctionComponent<CoreUxLoadingSkeletonProps> = ({
  type
}) => {
  if (type === SkeletonType.SWIMLANE_SINGLE) {
    return <SwimlaneSingleSkeleton />
  }

  if (type === SkeletonType.HERO) {
    return <HeroSkeleton />
  }

  if (type === SkeletonType.CATEGORY_FILTERS) {
    return <CategoryFiltersSkeleton />
  }

  if (type === SkeletonType.QUICK_FILTERS) {
    return <QuickFiltersSkeleton />
  }

  return <FallbackSkeleton />
}
